<template>
  <div class="return-reasons-container">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h2 class="page-title">
            <i class="fas fa-tags title-icon" />
            Gestion des catégories de tickets
          </h2>
          <p class="page-subtitle">
            Créez, éditez et organisez les catégories de tickets pour le support client.
          </p>
        </div>
        <b-button
          variant="primary"
          size="lg"
          class="add-button"
          @click="showAddModal = true"
        >
          <i class="fas fa-plus mr-2" />
          Ajouter une catégorie
        </b-button>
      </div>
    </div>

    <!-- Enhanced Top Section: Search, Filters, Results Summary -->
    <div class="enhanced-top-section">
      <div class="enhanced-stats-filters">
        <!-- Stats Cards -->
        <div class="enhanced-stats">
          <div class="enhanced-stat-card total">
            <span class="icon"><i class="fas fa-list" /></span>
            <span class="number">{{ stats.total }}</span>
            <span class="label">Total</span>
          </div>
        </div>
        <!-- Search -->
        <div class="enhanced-filters">
          <div class="enhanced-search">
            <span class="search-icon"><i class="fas fa-search" /></span>
            <input
              v-model="searchQuery"
              class="form-control search-input"
              type="text"
              placeholder="Rechercher une catégorie..."
            >
            <button
              v-if="searchQuery"
              class="btn clear-search"
              aria-label="Effacer la recherche"
              @click="searchQuery = ''"
            >
              <i class="fas fa-times" />
            </button>
          </div>
        </div>
      </div>
      <!-- Results Summary -->
      <div
        v-if="filteredCategories.length > 0"
        class="enhanced-results-summary"
      >
        <span class="results-count">
          {{ filteredCategories.length }} catégorie{{ filteredCategories.length > 1 ? 's' : '' }} trouvée{{ filteredCategories.length > 1 ? 's' : '' }}
        </span>
      </div>
    </div>

    <!-- Table Card -->
    <b-card class="table-card">
      <div
        v-if="filteredCategories.length === 0"
        class="empty-state"
      >
        <div class="empty-icon">
          <i class="fas fa-search" />
        </div>
        <h4>Aucune catégorie trouvée</h4>
        <p v-if="searchQuery">
          Aucune catégorie ne correspond à votre recherche "{{ searchQuery }}"
        </p>
        <p v-else>
          Commencez par ajouter votre première catégorie de ticket
        </p>
        <b-button
          v-if="!searchQuery"
          variant="primary"
          @click="showAddModal = true"
        >
          <i class="fas fa-plus mr-2" />
          Ajouter la première catégorie
        </b-button>
      </div>

      <div
        v-else
        class="table-responsive"
      >
        <b-table
          :items="filteredCategories"
          :fields="enhancedFields"
          hover
          class="modern-table"
        >
          <template #cell(name)="{ item }">
            <div class="reason-content">
              <strong>{{ item.name }}</strong>
              <div
                v-if="item.display_name"
                class="text-muted small"
              >
                Nom affiché : {{ item.display_name }}
              </div>
            </div>
          </template>
          <template #cell(description)="{ item }">
            <span>{{ item.description }}</span>
          </template>
          <template #cell(actions)="{ item }">
            <div class="action-buttons">
              <b-button
                variant="outline-primary"
                class="edit-btn action-btn"
                size="sm"
                @click="editCategory(item)"
              >
                <i class="fas fa-edit" /> <span class="btn-text">Éditer</span>
              </b-button>
              <b-button
                variant="outline-danger"
                class="delete-btn action-btn"
                size="sm"
                @click="deleteCategory(item)"
              >
                <i class="fas fa-trash" /> <span class="btn-text">Supprimer</span>
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showAddModal"
      :title="isEditing ? 'Éditer la catégorie' : 'Ajouter une nouvelle catégorie'"
      size="lg"
      header-class="modal-header-custom"
      body-class="modal-body-custom"
      @hidden="resetForm"
      @ok="saveCategory"
    >
      <template #modal-header="{ close }">
        <div class="modal-header-content">
          <div class="modal-title">
            <i
              :class="isEditing ? 'fas fa-edit' : 'fas fa-plus'"
              class="modal-icon"
            />
            {{ isEditing ? 'Éditer la catégorie' : 'Ajouter une nouvelle catégorie' }}
          </div>
          <b-button
            variant="link"
            class="modal-close"
            @click="close()"
          >
            <i class="fas fa-times" />
          </b-button>
        </div>
      </template>

      <div class="modal-form">
        <div class="form-section">
          <h5 class="section-title">
            Informations générales
          </h5>
          <b-form-group
            label="Nom de la catégorie"
            label-for="category-name"
            class="form-group-enhanced"
          >
            <b-form-input
              id="category-name"
              v-model="editForm.name"
              placeholder="Ex: Technique, Commercial, Livraison..."
              required
              class="form-input-enhanced"
              :state="editForm.name ? true : null"
            />
          </b-form-group>
          <b-form-group
            label="Nom affiché (optionnel)"
            label-for="category-display-name"
            class="form-group-enhanced"
          >
            <b-form-input
              id="category-display-name"
              v-model="editForm.display_name"
              placeholder="Nom affiché dans l'interface (optionnel)"
              class="form-input-enhanced"
            />
          </b-form-group>
          <b-form-group
            label="Description"
            label-for="category-description"
            class="form-group-enhanced"
          >
            <b-form-textarea
              id="category-description"
              v-model="editForm.description"
              rows="2"
              placeholder="Décrivez l'usage de cette catégorie..."
              class="form-input-enhanced"
            />
          </b-form-group>
        </div>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <div class="modal-footer-custom">
          <b-button
            variant="secondary"
            @click="cancel()"
          >
            <i class="fas fa-times mr-2" />
            Annuler
          </b-button>
          <b-button
            variant="primary"
            :disabled="!editForm.name"
            class="save-btn"
            @click="ok()"
          >
            <i class="fas fa-save mr-2" />
            {{ isEditing ? 'Mettre à jour' : 'Ajouter' }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard, BButton, BFormGroup, BFormInput, BFormTextarea, BTable, BModal,
} from 'bootstrap-vue'

export default {
  name: 'TicketCategories',
  components: {
    BCard, BButton, BFormGroup, BFormInput, BFormTextarea, BTable, BModal,
  },
  data() {
    return {
      categories: [],
      showAddModal: false,
      searchQuery: '',
      stats: { total: 0 },
      editForm: {
        id: null, name: '', display_name: '', description: '',
      },
      enhancedFields: [
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'description', label: 'Description' },
        { key: 'actions', label: 'Actions', class: 'actions-column' },
      ],
    }
  },
  computed: {
    filteredCategories() {
      let filtered = this.categories
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(c => (c.name && c.name.toLowerCase().includes(query))
          || (c.display_name && c.display_name.toLowerCase().includes(query))
          || (c.description && c.description.toLowerCase().includes(query)))
      }
      return filtered
    },
    isEditing() {
      return this.editForm.id !== null
    },
  },
  created() {
    this.fetchCategories()
  },
  methods: {
    async fetchCategories() {
      try {
        const { data } = await axios.get('/api/support/categories/')
        this.categories = data
        this.stats.total = Array.isArray(data) ? data.length : 0
      } catch (error) {
        if (this.$bvToast) {
          this.$bvToast.toast('Erreur lors du chargement des catégories', {
            title: 'Erreur',
            variant: 'danger',
          })
        }
      }
    },
    editCategory(item) {
      this.editForm = { ...item }
      this.showAddModal = true
    },
    resetForm() {
      this.editForm = {
        id: null, name: '', display_name: '', description: '',
      }
    },
    async saveCategory() {
      try {
        if (this.editForm.id) {
          await axios.put(`/api/support/categories/${this.editForm.id}/`, this.editForm)
          if (this.$bvToast) {
            this.$bvToast.toast('Catégorie mise à jour avec succès', {
              title: 'Succès',
              variant: 'success',
            })
          }
        } else {
          await axios.post('/api/support/categories/', this.editForm)
          if (this.$bvToast) {
            this.$bvToast.toast('Catégorie ajoutée avec succès', {
              title: 'Succès',
              variant: 'success',
            })
          }
        }
        this.showAddModal = false
        this.resetForm()
        this.fetchCategories()
      } catch (error) {
        if (this.$bvToast) {
          this.$bvToast.toast('Erreur lors de la sauvegarde', {
            title: 'Erreur',
            variant: 'danger',
          })
        }
      }
    },
    async deleteCategory(item) {
      this.$bvModal.msgBoxConfirm(
        `Êtes-vous sûr de vouloir supprimer la catégorie "${item.name}" ?`,
        {
          title: 'Confirmation',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
          footerClass: 'p-2',
          centered: true,
        },
      ).then(async confirmed => {
        if (confirmed) {
          try {
            await axios.delete(`/api/support/categories/${item.id}/`)
            if (this.$bvToast) {
              this.$bvToast.toast('Catégorie supprimée', {
                title: 'Succès',
                variant: 'success',
              })
            }
            this.fetchCategories()
          } catch (error) {
            if (this.$bvToast) {
              this.$bvToast.toast('Erreur lors de la suppression', {
                title: 'Erreur',
                variant: 'danger',
              })
            }
          }
        }
      })
    },
  },
}
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css');
.return-reasons-container {
  padding: 1.5rem;
  background: #f8f9fa;
  min-height: 100vh;
}
.page-header {
  background: linear-gradient(90deg, #f7fafc 0%, #e3e8ee 100%);
  border-radius: 12px;
  padding: 2rem;
  color: #1a202c;
  margin-bottom: 2rem;
  box-shadow: 0 4px 15px rgba(227, 6, 19, 0.08);
  border: 1.5px solid #e3e8ee;
}
.page-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #1a202c;
}
.title-icon {
  font-size: 1.75rem;
  opacity: 0.9;
  color: #e30613;
}
.page-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 1.1rem;
  color: #4a5568;
}
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}
.title-section {
  flex: 1;
}
.page-title {
  font-size: 2rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.title-icon {
  font-size: 1.75rem;
  opacity: 0.9;
}
.page-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 1.1rem;
}
.add-button {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  font-weight: 500;
}
.add-button:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}
.enhanced-top-section {
  margin-bottom: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(220, 38, 38, 0.10);
  padding: 1.5rem 2rem 1rem 2rem;
}
.enhanced-stats-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
}
.enhanced-stats {
  display: flex;
  gap: 1.25rem;
}
.enhanced-stat-card {
  display: flex;
  align-items: center;
  background: #f9f6f6;
  border-radius: 10px;
  padding: 1rem 1.5rem;
  box-shadow: 0 1px 4px rgba(220,38,38,0.07);
  min-width: 120px;
  gap: 0.75rem;
  border: 1.5px solid #e5e7eb;
}
.enhanced-stat-card .icon {
  font-size: 1.5rem;
  color: #e30613;
  margin-right: 0.5rem;
}
.enhanced-stat-card .number {
  font-size: 1.3rem;
  font-weight: 700;
  color: #e30613;
}
.enhanced-stat-card .label {
  font-size: 0.9rem;
  color: #222;
  font-weight: 500;
}
.enhanced-filters {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 260px;
}
.enhanced-search {
  display: flex;
  align-items: center;
  position: relative;
}
.enhanced-search .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #e30613;
}
.enhanced-search .search-input {
  padding-left: 38px;
  border-radius: 8px;
  border: 2px solid #e30613;
  transition: border-color 0.3s ease;
}
.enhanced-search .search-input:focus {
  border-color: #e30613;
}
.enhanced-search .clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #e30613;
  padding: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}
.enhanced-results-summary {
  margin-top: 1rem;
  color: #e30613;
  font-weight: 500;
  font-size: 1rem;
}
.table-card {
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
}
.modern-table {
  margin: 0;
}
.modern-table thead th {
  background: #f8f9fa;
  border: none;
  font-weight: 600;
  color: #495057;
  padding: 1rem;
  border-bottom: 2px solid #e9ecef;
}
.modern-table tbody td {
  padding: 1rem;
  vertical-align: middle;
  border-top: 1px solid #f1f3f4;
}
.modern-table tbody tr:hover {
  background: #f8f9fa;
}
.active-row {
  background: rgba(40, 167, 69, 0.02);
}
.inactive-row {
  background: rgba(108, 117, 125, 0.02);
}
.reason-content strong {
  color: #495057;
  font-weight: 600;
}
.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.action-btn {
  border-radius: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}
.action-btn:hover {
  transform: translateY(-1px);
}
.edit-btn:hover {
  background: #007bff;
  border-color: #007bff;
  color: white;
}
.delete-btn:hover {
  background: #dc3545;
  border-color: #dc3545;
  color: white;
}
.empty-state {
  text-align: center;
  padding: 3rem 2rem;
  color: #6c757d;
}
.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}
.empty-state h4 {
  color: #495057;
  margin-bottom: 1rem;
}
.empty-state p {
  margin-bottom: 1.5rem;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}
.modal-header-custom {
  border: none;
  padding: 2rem 3rem 1.5rem 3rem;
  background: linear-gradient(135deg, #e30613 0%, #222 100%);
  border-radius: 0.5rem 0.5rem 0 0;
}
.modal-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  color: white;
}
.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.modal-icon {
  font-size: 1.125rem;
}
.modal-close {
  color: white;
  opacity: 0.8;
  transition: opacity 0.3s ease;
  padding: 0.5rem;
  border-radius: 50%;
}
.modal-close:hover {
  opacity: 1;
  color: white;
  background: rgba(255, 255, 255, 0.1);
}
.modal-body-custom {
  padding: 2.5rem 3rem;
}
.modal-form {
  max-height: 70vh;
  overflow-y: auto;
}
.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e9ecef;
}
.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}
.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.form-group-enhanced {
  margin-bottom: 1.5rem;
}
.form-input-enhanced {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
  transition: all 0.3s ease;
}
.form-input-enhanced:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}
.form-input-enhanced:disabled {
  background: #f8f9fa;
  opacity: 0.6;
}
.modal-footer-custom {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e9ecef;
}
.save-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  transition: transform 0.3s ease;
}
.save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  background: linear-gradient(135deg, #5a6fd8, #6a42a0);
}
.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
@media (max-width: 1200px) {
  .enhanced-filters {
    min-width: 0;
  }
}
@media (max-width: 992px) {
  .enhanced-top-section {
    padding: 1rem;
  }
  .enhanced-stats-filters {
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
  }
  .enhanced-stats {
    justify-content: flex-start;
  }
}
@media (max-width: 768px) {
  .return-reasons-container {
    padding: 1rem;
  }
  .page-header {
    padding: 1.5rem;
  }
  .header-content {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
  .page-title {
    font-size: 1.5rem;
    justify-content: center;
  }
  .enhanced-stats {
    flex-direction: column;
    gap: 0.5rem;
  }
  .enhanced-top-section {
    padding: 0.5rem;
  }
  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }
  .action-btn .btn-text {
    display: inline;
  }
  .modal-body-custom {
    padding: 1.5rem;
  }
  .modal-footer-custom {
    padding: 1rem 1.5rem;
    flex-direction: column-reverse;
    gap: 0.75rem;
  }
  .modal-footer-custom .btn {
    width: 100%;
    justify-content: center;
  }
}
@media (max-width: 576px) {
  .enhanced-search {
    max-width: 100%;
  }
  .action-btn .btn-text {
    display: none;
  }
  .action-btn {
    min-width: 40px;
    justify-content: center;
  }
}
</style>
